import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertCircle, Loader2 } from 'lucide-react';
import { useFormatting } from '@/hooks/useFormatting';
import { interpretQueryWithContext, ContextualQueryInterpretation, generateSuggestedFollowUps, QueryError } from '@/services/queryInterpreterWithContext';
import { queryInvoices, Invoice } from '@/services/invoiceQueryService';
import { prioritizeInvoices, getPriorityEmoji, getPriorityLabel } from '@/services/invoicePrioritization';
import { ConversationState, QueryContext } from '@/types/conversation';

interface QueryResultsViewProps {
  query: string;
  onBack: () => void;
  onSubmitFollowUp: (query: string) => void;
  conversationState: ConversationState;
  onUpdateResults: (results: any[], summary: string) => void;
}

type QueryState = 
  | { status: 'loading' }
  | { status: 'error'; error: QueryError }
  | { status: 'success'; invoices: Invoice[]; interpretation: ContextualQueryInterpretation; isPrioritized?: boolean };

export function QueryResultsView({ query, onBack, onSubmitFollowUp, conversationState, onUpdateResults }: QueryResultsViewProps) {
  const { formatDate, formatCurrency } = useFormatting();
  const [queryState, setQueryState] = useState<QueryState>({ status: 'loading' });
  const [suggestedFollowUps, setSuggestedFollowUps] = useState<string[]>([]);

  useEffect(() => {
    async function processQuery() {
      setQueryState({ status: 'loading' });

      console.log('═══════════════════════════════════════════════════════');
      console.log('[QueryResultsView] 🔍 PROCESSING QUERY:', query);
      console.log('[QueryResultsView] 📊 Conversation state:', {
        currentQuery: conversationState.currentQuery,
        previousQuery: conversationState.previousQuery,
        currentResultsCount: (conversationState.currentResults || []).length,
        previousResultsCount: (conversationState.previousResults || []).length,
        historyLength: conversationState.history.length,
      });

      // Build context from conversation state
      const context: QueryContext = {
        previousQuery: conversationState.previousQuery,
        previousResults: conversationState.previousResults || [],
        currentResults: conversationState.currentResults || [],
        resultCount: (conversationState.currentResults || []).length,
        totalAmount: (conversationState.currentResults || []).reduce((sum: number, inv: any) => sum + inv.amount, 0),
      };

      console.log('[QueryResultsView] 🎯 Query context built:', {
        previousQuery: context.previousQuery,
        currentResultsCount: context.currentResults.length,
        previousResultsCount: context.previousResults.length,
        resultCount: context.resultCount,
        totalAmount: context.totalAmount,
      });

      if (context.currentResults && context.currentResults.length > 0) {
        console.log('[QueryResultsView] 📋 Current results summary:', 
          context.currentResults.map((inv: any) => ({
            invoiceNumber: inv.invoiceNumber,
            customerName: inv.customerName,
            amount: inv.amount,
          }))
        );
      }

      // Step 1: Interpret query with context
      console.log('[QueryResultsView] 🤖 Calling interpretQueryWithContext...');
      const interpretation = await interpretQueryWithContext(query, context);
      console.log('[QueryResultsView] 📝 Interpretation result:', interpretation);

      // Check if error
      if ('error' in interpretation) {
        console.log('[QueryResultsView] Query interpretation error:', interpretation);
        setQueryState({ status: 'error', error: interpretation });
        return;
      }

      console.log('[QueryResultsView] Query interpretation:', interpretation);

      // Step 2: Handle different follow-up types
      let invoices: Invoice[] = [];
      let isPrioritized = false;

      if (interpretation.isFollowUp && interpretation.followUpType === 'prioritization') {
        // Use current results for prioritization
        invoices = context.currentResults || [];
        isPrioritized = true;
        console.log('[QueryResultsView] Prioritization query - using current results:', invoices.length);
      } else if (interpretation.isFollowUp && interpretation.followUpType === 'filtering') {
        // Filter current results
        const currentResults = context.currentResults || [];
        invoices = applyFiltersToResults(currentResults, interpretation);
        console.log('[QueryResultsView] Filtering current results:', invoices.length);
      } else if (interpretation.isFollowUp && interpretation.followUpType === 'reset') {
        // Return to previous results
        invoices = context.previousResults || [];
        console.log('[QueryResultsView] Reset to previous results:', invoices.length);
      } else {
        // New query - query from full dataset
        invoices = queryInvoices(interpretation);
        console.log('[QueryResultsView] New query - filtered invoices:', invoices.length);
      }

      // Step 3: Check if no results
      if (invoices.length === 0) {
        setQueryState({
          status: 'error',
          error: {
            error: true,
            message: 'No invoices match that query.',
            suggestion: 'Try different filters or ask: "Show me all overdue invoices"',
          },
        });
        return;
      }

      // Step 4: Success - show results
      setQueryState({
        status: 'success',
        invoices,
        interpretation,
        isPrioritized,
      });

      // Step 5: Update conversation with results
      const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);
      const summary = `${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}, ${formatCurrency(totalAmount)}`;
      onUpdateResults(invoices, summary);

      // Step 6: Generate suggested follow-ups
      const suggestions = generateSuggestedFollowUps(invoices, context);
      setSuggestedFollowUps(suggestions);
    }

    processQuery();
  }, [query]);

  // Helper function to apply filters to existing results
  function applyFiltersToResults(results: Invoice[], interpretation: ContextualQueryInterpretation): Invoice[] {
    // Safety check - ensure results is an array
    if (!results || !Array.isArray(results)) {
      console.error('[applyFiltersToResults] ❌ Results is undefined or not an array:', results);
      return [];
    }
    
    console.log('[applyFiltersToResults] Starting with results:', results.length);
    console.log('[applyFiltersToResults] Filters:', interpretation.filters);
    let filtered = [...results];

    // Apply filters
    if (interpretation.filters.customerName) {
      const searchTerm = interpretation.filters.customerName.toLowerCase();
      console.log('[applyFiltersToResults] Filtering by customer:', searchTerm);
      filtered = filtered.filter(inv => {
        const matches = inv.customerName.toLowerCase().includes(searchTerm);
        console.log(`[applyFiltersToResults] ${inv.customerName} includes "${searchTerm}"?`, matches);
        return matches;
      });
      console.log('[applyFiltersToResults] After customer filter:', filtered.length);
    }

    if (interpretation.filters.minAmount) {
      filtered = filtered.filter(inv => inv.amount >= interpretation.filters.minAmount!);
    }

    if (interpretation.filters.maxAmount) {
      filtered = filtered.filter(inv => inv.amount <= interpretation.filters.maxAmount!);
    }

    if (interpretation.filters.minDaysOverdue) {
      filtered = filtered.filter(inv => inv.daysOverdue >= interpretation.filters.minDaysOverdue!);
    }

    // Apply sorting
    if (interpretation.sortBy) {
      filtered.sort((a, b) => {
        const aVal = a[interpretation.sortBy!];
        const bVal = b[interpretation.sortBy!];
        const order = interpretation.sortOrder === 'desc' ? -1 : 1;
        return aVal > bVal ? order : -order;
      });
    }

    // If "show only oldest/newest", take first result
    if (interpretation.userIntent.includes('only')) {
      filtered = filtered.slice(0, 1);
    }

    return filtered;
  }

  // Loading state
  if (queryState.status === 'loading') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              onClick={onBack}
              variant="outline"
              size="lg"
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div className="text-sm text-muted-foreground">
              Query: <span className="font-medium text-foreground">&quot;{query}&quot;</span>
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-blue-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              <p className="text-sm">
                Understanding your query...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (queryState.status === 'error') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              onClick={onBack}
              variant="outline"
              size="lg"
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div className="text-sm text-muted-foreground">
              Query: <span className="font-medium text-foreground">&quot;{query}&quot;</span>
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-amber-600">
                <AlertCircle className="h-5 w-5" />
                <p className="text-sm font-medium">
                  {queryState.error.message}
                </p>
              </div>
              {queryState.error.suggestion && (
                <p className="text-sm text-muted-foreground ml-8">
                  {queryState.error.suggestion}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state - show results
  const { invoices, interpretation, isPrioritized } = queryState;
  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);

  // If prioritization query, show prioritized view
  if (isPrioritized) {
    const prioritization = prioritizeInvoices(invoices);

    return (
      <div className="p-6 space-y-6 animate-in fade-in duration-300">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              onClick={onBack}
              variant="outline"
              size="lg"
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div className="text-sm text-muted-foreground">
              Query: <span className="font-medium text-foreground">&quot;{query}&quot;</span>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              Priority Chase List ({invoices.length} invoice{invoices.length !== 1 ? 's' : ''})
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              {prioritization.summary}
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {prioritization.prioritized.map((item, index) => (
                <div key={item.invoice.invoiceId} className="space-y-2">
                  {/* Priority header (show once per priority level) */}
                  {(index === 0 || item.priority !== prioritization.prioritized[index - 1].priority) && (
                    <div className="flex items-center gap-2 mt-4 mb-3">
                      <span className="text-lg">{getPriorityEmoji(item.priority)}</span>
                      <span className="font-semibold text-sm">{getPriorityLabel(item.priority)}</span>
                    </div>
                  )}

                  {/* Invoice card */}
                  <div className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-lg">{index + 1}.</span>
                          <div>
                            <div className="font-semibold">
                              {item.invoice.invoiceNumber} ({item.invoice.customerName})
                            </div>
                            <div className="text-2xl font-bold text-primary mt-1">
                              {formatCurrency(item.invoice.amount)}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground ml-8">
                          {item.invoice.daysOverdue} days overdue | {item.reasoning}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline">
                          Send Reminder
                        </Button>
                        <Button size="sm" variant="ghost">
                          Mark Contacted
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Total Summary */}
              <div className="pt-4 mt-4 border-t-2">
                <div className="flex items-center justify-between">
                  <div className="text-lg font-semibold">
                    Total to Recover
                  </div>
                  <div className="text-2xl font-bold text-primary">
                    {formatCurrency(prioritization.totalAmount)}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Suggested follow-ups */}
        {suggestedFollowUps.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground">💡 You might also want to:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedFollowUps.map((suggestion, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      onClick={() => onSubmitFollowUp(suggestion)}
                      className="text-xs"
                    >
                      &quot;{suggestion}&quot;
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Standard results view (not prioritized)
  return (
    <div className="p-6 space-y-6 animate-in fade-in duration-300">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            onClick={onBack}
            variant="outline"
            size="lg"
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
          <div className="text-sm text-muted-foreground">
            Query: <span className="font-medium text-foreground">&quot;{query}&quot;</span>
          </div>
        </div>
      </div>

      {/* Context indicator for follow-ups */}
      {interpretation.isFollowUp && interpretation.contextUsed && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-blue-800">
              📎 {interpretation.contextUsed}
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">
            {interpretation.userIntent} ({invoices.length} result{invoices.length !== 1 ? 's' : ''})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Table Header */}
            <div className="grid grid-cols-5 gap-4 pb-3 border-b font-semibold text-sm">
              <div>Invoice</div>
              <div>Customer</div>
              <div className="text-right">Amount</div>
              <div className="text-right">Days Overdue</div>
              <div className="text-right">Due Date</div>
            </div>

            {/* Table Rows */}
            {invoices.map((invoice) => (
              <div 
                key={invoice.invoiceId}
                className="grid grid-cols-5 gap-4 py-3 border-b last:border-0 hover:bg-muted/50 transition-colors"
              >
                <div className="font-medium">{invoice.invoiceNumber}</div>
                <div className="text-sm">{invoice.customerName}</div>
                <div className="text-right font-semibold">
                  {formatCurrency(invoice.amount)}
                </div>
                <div className="text-right">
                  <span className="inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    {invoice.daysOverdue} days
                  </span>
                </div>
                <div className="text-right text-sm text-muted-foreground">
                  {formatDate(new Date(invoice.dueDate), {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </div>
              </div>
            ))}

            {/* Total Summary */}
            <div className="pt-4 mt-4 border-t-2">
              <div className="flex items-center justify-between">
                <div className="text-lg font-semibold">
                  Total Outstanding
                </div>
                <div className="text-2xl font-bold text-primary">
                  {formatCurrency(totalAmount)}
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Total from {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Suggested follow-ups */}
      {suggestedFollowUps.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              <p className="text-sm font-medium text-muted-foreground">💡 You might also want to:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedFollowUps.map((suggestion, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    onClick={() => onSubmitFollowUp(suggestion)}
                    className="text-xs"
                  >
                    &quot;{suggestion}&quot;
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
